import React, { useMemo } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';

/**
 * Pack-type specific required fields and validation rules
 */
const PACK_REQUIREMENTS = {
  product: [
    { id: 'name_en', label: 'Product Name (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Product Name (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'features', label: 'Features (min 2)', field: 'content.features', required: true, minItems: 2 },
    { id: 'benefits', label: 'Benefits (min 2)', field: 'content.benefits', required: true, minItems: 2 },
    { id: 'pricing', label: 'Pricing Info', field: 'content.pricing', required: true },
    { id: 'target_audience', label: 'Target Audience', field: 'content.target_audience', required: true },
  ],
  promotion: [
    { id: 'name_en', label: 'Promotion Name (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Promotion Name (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'campaign_name', label: 'Campaign Name', field: 'content.campaign_name', required: true },
    { id: 'offer_type', label: 'Offer Type', field: 'content.offer_type', required: true },
    { id: 'offer_details', label: 'Offer Details', field: 'content.offer_details', required: true },
    { id: 'start_date', label: 'Start Date', field: 'content.start_date', required: true },
    { id: 'end_date', label: 'End Date', field: 'content.end_date', required: true },
    { id: 'product_link', label: 'Linked Product', field: 'relationships.linked_packs', required: true, minItems: 1 },
  ],
  direction: [
    { id: 'name_en', label: 'Direction Name', field: 'name.en', required: true },
    { id: 'description_en', label: 'Description', field: 'description.en', required: true },
    { id: 'strategic_goals', label: 'Strategic Goals', field: 'content.strategic_goals', required: true, minItems: 1 },
    { id: 'key_messages', label: 'Key Messages', field: 'content.key_messages', required: true, minItems: 1 },
    { id: 'target_segments', label: 'Target Segments', field: 'content.target_segments', required: true },
  ],
  values: [
    { id: 'name_en', label: 'Value Name (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Value Name (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'core_principles', label: 'Core Principles (min 2)', field: 'content.core_principles', required: true, minItems: 2 },
    { id: 'behavioral_guidelines', label: 'Behavioral Guidelines (min 2)', field: 'content.behavioral_guidelines', required: true, minItems: 2 },
  ],
  compliance: [
    { id: 'name_en', label: 'Compliance Rule (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Compliance Rule (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'regulation_name', label: 'Regulation Name', field: 'content.regulation_name', required: true },
    { id: 'mandatory_phrases', label: 'Mandatory Phrases', field: 'content.mandatory_phrases', required: false },
    { id: 'prohibited_phrases', label: 'Prohibited Phrases (min 1)', field: 'content.prohibited_phrases', required: true, minItems: 1 },
    { id: 'safe_alternatives', label: 'Safe Alternatives', field: 'content.safe_alternatives', required: true },
  ],
  services: [
    { id: 'name_en', label: 'Service Name (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Service Name (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'service_overview', label: 'Service Overview', field: 'content.service_overview', required: true },
    { id: 'service_features', label: 'Service Features (min 2)', field: 'content.service_features', required: true, minItems: 2 },
  ],
  viral: [
    { id: 'name_en', label: 'Viral Pattern Name', field: 'name.en', required: true },
    { id: 'description_en', label: 'Description', field: 'description.en', required: true },
    { id: 'viral_hook', label: 'Viral Hook/Pattern', field: 'content.viral_hook', required: true },
    { id: 'emotional_triggers', label: 'Emotional Triggers (min 1)', field: 'content.emotional_triggers', required: true, minItems: 1 },
  ],
  trend: [
    { id: 'name_en', label: 'Trend Name', field: 'name.en', required: true },
    { id: 'description_en', label: 'Description', field: 'description.en', required: true },
    { id: 'trend_name', label: 'Trend Title', field: 'content.trend_name', required: true },
    { id: 'trend_summary', label: 'Trend Summary', field: 'content.trend_summary', required: true },
    { id: 'key_signals', label: 'Key Signals (min 1)', field: 'content.key_signals', required: true, minItems: 1 },
  ],
  ambassador: [
    { id: 'name_en', label: 'Ambassador Pack Name (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Ambassador Pack Name (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'ambassador_name', label: 'Ambassador Name', field: 'content.ambassador_name', required: true },
    { id: 'role_title', label: 'Role/Title', field: 'content.role_title', required: true },
    { id: 'bio', label: 'Bio/Background', field: 'content.bio', required: true },
    { id: 'key_achievements', label: 'Key Achievements (min 1)', field: 'content.key_achievements', required: true, minItems: 1 },
  ],
  health: [
    { id: 'name_en', label: 'Health Claim Name (EN)', field: 'name.en', required: true },
    { id: 'name_ms', label: 'Health Claim Name (MS)', field: 'name.ms', required: true },
    { id: 'description_en', label: 'Description (EN)', field: 'description.en', required: true },
    { id: 'description_ms', label: 'Description (MS)', field: 'description.ms', required: true },
    { id: 'health_topic', label: 'Health Topic', field: 'content.health_topic', required: true },
    { id: 'health_category', label: 'Health Claim Category', field: 'content.health_category', required: true },
    { id: 'approved_claims', label: 'Approved Health Claims (min 1)', field: 'content.approved_claims', required: true, minItems: 1 },
    { id: 'prohibited_claims', label: 'Prohibited Claims (min 1)', field: 'content.prohibited_claims', required: true, minItems: 1 },
    { id: 'safe_phrasing', label: 'Safe Alternative Phrasing', field: 'content.safe_phrasing', required: true },
  ],
};

/**
 * CompletenessChecklist - Fixed right-side panel showing completion status
 * @param {string} packType - Current pack type
 * @param {Object} formData - Current form data
 * @param {Function} onJumpToField - Callback to focus on missing field
 * @param {Function} onScoreChange - Callback to pass completion score to parent
 */
const CompletenessChecklist = ({ packType, formData, onJumpToField, onScoreChange }) => {
  const requirements = PACK_REQUIREMENTS[packType] || [];

  // Calculate completion status for each requirement
  const checklistItems = useMemo(() => {
    return requirements.map(req => {
      const value = getNestedValue(formData, req.field);
      let isComplete = false;

      if (Array.isArray(value)) {
        isComplete = value.length > 0 && (!req.minItems || value.length >= req.minItems);
      } else if (typeof value === 'object' && value !== null) {
        isComplete = Object.keys(value).length > 0;
      } else {
        isComplete = value && value.toString().trim().length > 0;
      }

      return {
        ...req,
        isComplete,
        value,
      };
    });
  }, [formData, requirements]);

  // Calculate overall completion score
  const completionScore = useMemo(() => {
    const requiredItems = checklistItems.filter(item => item.required);
    const completedRequired = requiredItems.filter(item => item.isComplete);
    return requiredItems.length > 0 
      ? Math.round((completedRequired.length / requiredItems.length) * 100)
      : 100;
  }, [checklistItems]);

  const incompleteItems = checklistItems.filter(item => item.required && !item.isComplete);

  // Notify parent of score changes
  React.useEffect(() => {
    if (onScoreChange) {
      onScoreChange(completionScore);
    }
  }, [completionScore, onScoreChange]);

  const handleFixAll = () => {
    if (incompleteItems.length > 0 && onJumpToField) {
      onJumpToField(incompleteItems[0].field);
    }
  };

  return (
    <div style={{
      position: 'sticky',
      top: '16px',
      height: 'fit-content',
      background: EMZI_COLORS.background,
      border: `1px solid ${EMZI_COLORS.border}`,
      borderRadius: EMZI_RADIUS.lg,
      padding: EMZI_SPACING.md,
      maxHeight: 'calc(100vh - 100px)',
      overflowY: 'auto',
    }}>
      {/* Header with score */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: EMZI_SPACING.md,
        paddingBottom: EMZI_SPACING.sm,
        borderBottom: `1px solid ${EMZI_COLORS.border}`,
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: '600',
          color: EMZI_COLORS.textPrimary,
        }}>
          📋 Completeness
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: EMZI_SPACING.xs,
        }}>
          <span style={{
            fontSize: '20px',
            fontWeight: '700',
            color: completionScore >= 90 ? EMZI_COLORS.success : 
                   completionScore >= 70 ? EMZI_COLORS.warning : 
                   EMZI_COLORS.error,
          }}>
            {completionScore}%
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        height: '8px',
        background: EMZI_COLORS.surface,
        borderRadius: EMZI_RADIUS.full,
        overflow: 'hidden',
        marginBottom: EMZI_SPACING.md,
      }}>
        <div style={{
          height: '100%',
          width: `${completionScore}%`,
          background: completionScore >= 90 ? EMZI_COLORS.success : 
                     completionScore >= 70 ? EMZI_COLORS.warning : 
                     EMZI_COLORS.error,
          transition: 'width 0.3s ease',
        }} />
      </div>

      {/* Fix all button */}
      {incompleteItems.length > 0 && (
        <button
          type="button"
          onClick={handleFixAll}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            background: EMZI_COLORS.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: EMZI_SPACING.md,
          }}
        >
          🔧 Fix {incompleteItems.length} Missing Field{incompleteItems.length > 1 ? 's' : ''}
        </button>
      )}

      {/* Checklist items */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: EMZI_SPACING.xs,
      }}>
        {checklistItems.map((item) => (
          <div
            key={item.id}
            onClick={() => onJumpToField && onJumpToField(item.field)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: EMZI_SPACING.xs,
              padding: EMZI_SPACING.xs,
              background: item.isComplete ? EMZI_COLORS.surface : 'transparent',
              borderRadius: EMZI_RADIUS.sm,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            <span style={{
              fontSize: '16px',
              flexShrink: 0,
            }}>
              {item.isComplete ? '✅' : item.required ? '⭕' : '○'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '13px',
                color: item.isComplete ? EMZI_COLORS.textPrimary : EMZI_COLORS.textSecondary,
                fontWeight: item.required ? '500' : '400',
              }}>
                {item.label}
                {!item.required && (
                  <span style={{
                    marginLeft: EMZI_SPACING.xs,
                    fontSize: '11px',
                    color: EMZI_COLORS.textMuted,
                  }}>
                    (optional)
                  </span>
                )}
              </div>
              {item.minItems && !item.isComplete && (
                <div style={{
                  fontSize: '11px',
                  color: EMZI_COLORS.warning,
                  marginTop: '2px',
                }}>
                  Min: {item.minItems} items
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Bilingual indicator */}
      {['product', 'promotion', 'values', 'compliance', 'services', 'ambassador', 'health'].includes(packType) && (
        <div style={{
          marginTop: EMZI_SPACING.md,
          padding: EMZI_SPACING.sm,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            🌐 Bilingual Required
          </div>
          This pack type requires both EN and MS translations for customer-facing content.
        </div>
      )}
      
      {/* Single language indicator */}
      {['direction', 'trend'].includes(packType) && (
        <div style={{
          marginTop: EMZI_SPACING.md,
          padding: EMZI_SPACING.sm,
          background: '#EFF6FF',
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '12px',
          color: '#1E40AF',
          borderLeft: `3px solid ${EMZI_COLORS.info}`,
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            📝 Single Language (EN)
          </div>
          This pack type is for internal strategy - English only is sufficient.
        </div>
      )}
      
      {/* Flexible language indicator */}
      {['viral'].includes(packType) && (
        <div style={{
          marginTop: EMZI_SPACING.md,
          padding: EMZI_SPACING.sm,
          background: '#FEF3C7',
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '12px',
          color: '#92400E',
          borderLeft: `3px solid ${EMZI_COLORS.warning}`,
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            ✨ Flexible Language
          </div>
          Viral packs can be single-language with optional bilingual hooks based on what resonates best.
        </div>
      )}

      {/* Completion message */}
      {completionScore === 100 && (
        <div style={{
          marginTop: EMZI_SPACING.md,
          padding: EMZI_SPACING.sm,
          background: '#e6f7ed',
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '13px',
          color: EMZI_COLORS.success,
          textAlign: 'center',
          fontWeight: '500',
        }}>
          🎉 All required fields complete!
        </div>
      )}
    </div>
  );
};

// Helper function to get nested values from object
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export default CompletenessChecklist;
